import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getAgencies, getServices, getIndustries } from '../services/firestoreService';
import PageHead from '../components/Common/PageHead';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faChevronDown,
  faUserShield,
  faClipboardList,
  faUsers,
  faGlobe,
  faChevronUp,
  faStar,
  faArrowRight,
  faArrowLeft,
} from '@fortawesome/free-solid-svg-icons';
import Top from '../components/Common/Top';
import NewsletterSignup from '../utils/NewsletterSignup';
import { Helmet } from 'react-helmet-async';

const itemsPerPage = 5;

const AgencyArchive = () => {
  const [agencies, setAgencies] = useState([]);
  const [filteredAgencies, setFilteredAgencies] = useState([]);
  const [services, setServices] = useState([]);
  const [industries, setIndustries] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [mode, setMode] = useState('choice');

  const [searchTerm, setSearchTerm] = useState('');
  const [searchOption, setSearchOption] = useState('name');
  const [sortBy, setSortBy] = useState('latest');
  const [currentPage, setCurrentPage] = useState(1);

  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [showIndustriesDropdown, setShowIndustriesDropdown] = useState(false);
  const [showServicesDropdown, setShowServicesDropdown] = useState(false);

  const [activeIndex, setActiveIndex] = useState(null);

  const [guidedStep, setGuidedStep] = useState(0);
  const [guidedAnswers, setGuidedAnswers] = useState({
    projectType: '',
    projectGoals: '',
    industryPreference: '',
    servicePreferences: [],
    budgetRange: '',
  });
  const [recommendedAgencies, setRecommendedAgencies] = useState([]);

  const toggleAnswer = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [fetchedAgencies, servicesData, industriesData] = await Promise.all([
          getAgencies(),
          getServices(),
          getIndustries(),
        ]);
        setAgencies(fetchedAgencies);
        setFilteredAgencies(fetchedAgencies);
        setServices(servicesData);
        setIndustries(industriesData);
      } catch (err) {
        setError('Failed to fetch agencies.');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  useEffect(() => {
    let filtered = agencies;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((agency) => {
        if (searchOption === 'name') {
          return agency.name?.toLowerCase().includes(term);
        }
        if (searchOption === 'service') {
          return agency.services?.some((service) => service.toLowerCase().includes(term));
        }
        if (searchOption === 'industry') {
          return agency.industry?.toLowerCase().includes(term);
        }
        return true;
      });
    }

    if (selectedIndustries.length > 0) {
      filtered = filtered.filter((agency) => selectedIndustries.includes(agency.industry));
    }

    if (selectedServices.length > 0) {
      filtered = filtered.filter((agency) =>
        agency.services?.some((service) => selectedServices.includes(service))
      );
    }

    if (sortBy === 'latest') {
      filtered = [...filtered].sort(
        (a, b) => (b.dateCreated?.seconds || 0) - (a.dateCreated?.seconds || 0)
      );
    } else if (sortBy === 'oldest') {
      filtered = [...filtered].sort(
        (a, b) => (a.dateCreated?.seconds || 0) - (b.dateCreated?.seconds || 0)
      );
    }

    setFilteredAgencies(filtered);
    setCurrentPage(1);
  }, [searchTerm, searchOption, agencies, sortBy, selectedIndustries, selectedServices]);

  const totalPages = Math.ceil(filteredAgencies.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedAgencies = useMemo(
    () => filteredAgencies.slice(startIndex, startIndex + itemsPerPage),
    [filteredAgencies, startIndex]
  );

  const scrollToTop = () => {
    const el = document.getElementById('nextScroll');
    if (!el) return;
    window.scrollTo({
      top: el.offsetTop - 100,
      behavior: 'smooth',
    });
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => {
        const newPage = prevPage - 1;
        setTimeout(scrollToTop, 100);
        return newPage;
      });
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prevPage) => {
        const newPage = prevPage + 1;
        setTimeout(scrollToTop, 100);
        return newPage;
      });
    }
  };

  const toggleIndustry = (industry) => {
    setSelectedIndustries((prev) =>
      prev.includes(industry) ? prev.filter((i) => i !== industry) : [...prev, industry]
    );
  };

  const toggleService = (service) => {
    setSelectedServices((prev) =>
      prev.includes(service) ? prev.filter((s) => s !== service) : [...prev, service]
    );
  };

  const guidedStepsConfig = [
    {
      key: 'projectType',
      title: 'What is your project about?',
      description: 'Describe your AI project in a sentence or two.',
      type: 'textarea',
    },
    {
      key: 'projectGoals',
      title: 'What goals do you want to accomplish?',
      description: 'For example: automate tasks, improve customer support, increase revenue.',
      type: 'textarea',
    },
    {
      key: 'industryPreference',
      title: 'Which industry best describes your business?',
      description: 'Select the industry that is closest to what you do.',
      type: 'selectIndustry',
    },
    {
      key: 'servicePreferences',
      title: 'What kind of AI help are you looking for?',
      description: 'Choose the services that match your needs.',
      type: 'multiSelectServices',
    },
    {
      key: 'budgetRange',
      title: 'What is your approximate budget level?',
      description: 'This helps us suggest agencies comfortable with your scale.',
      type: 'selectBudget',
    },
  ];

  const handleGuidedChange = (key, value) => {
    setGuidedAnswers((prev) => ({ ...prev, [key]: value }));
  };

  const handleGuidedServiceToggle = (serviceName) => {
    setGuidedAnswers((prev) => {
      const exists = prev.servicePreferences.includes(serviceName);
      return {
        ...prev,
        servicePreferences: exists
          ? prev.servicePreferences.filter((s) => s !== serviceName)
          : [...prev.servicePreferences, serviceName],
      };
    });
  };

  const computeRecommendations = () => {
    let results = agencies.filter((a) => a.isApproved);

    if (guidedAnswers.industryPreference) {
      results = results.filter((a) => a.industry === guidedAnswers.industryPreference);
    }

    if (guidedAnswers.servicePreferences.length > 0) {
      results = results.filter((a) =>
        a.services?.some((s) => guidedAnswers.servicePreferences.includes(s))
      );
    }

    const text = `${guidedAnswers.projectType} ${guidedAnswers.projectGoals}`.toLowerCase();
    const keywords = text
      .split(/\s+/)
      .map((w) => w.trim())
      .filter((w) => w.length > 3);

    if (keywords.length > 0) {
      results = results.filter((a) => {
        const haystack = `${a.name || ''} ${a.description || ''} ${(a.services || []).join(
          ' '
        )}`.toLowerCase();
        return keywords.some((k) => haystack.includes(k));
      });
    }

    if (sortBy === 'latest') {
      results = [...results].sort(
        (a, b) => (b.dateCreated?.seconds || 0) - (a.dateCreated?.seconds || 0)
      );
    } else if (sortBy === 'oldest') {
      results = [...results].sort(
        (a, b) => (a.dateCreated?.seconds || 0) - (b.dateCreated?.seconds || 0)
      );
    }

    setRecommendedAgencies(results);
  };

  const handleGuidedNext = () => {
    if (guidedStep < guidedStepsConfig.length - 1) {
      setGuidedStep((s) => s + 1);
    } else {
      computeRecommendations();
      setMode("results");
    }
  };
  
  const handleGuidedBack = () => {
    if (guidedStep > 0) {
      setGuidedStep((s) => s - 1);
    } else {
      setMode('choice');
    }
  };

  const benefits = [
    {
      icon: faUserShield,
      title: 'Verified Agencies',
      description: 'All agencies are thoroughly vetted.',
    },
    {
      icon: faClipboardList,
      title: 'Detailed Profiles',
      description: 'Comprehensive information on each agency.',
    },
    {
      icon: faUsers,
      title: 'User Reviews',
      description: 'Honest feedback from clients.',
    },
    {
      icon: faGlobe,
      title: 'Wide Range of Services',
      description: 'Agencies specializing in various AI solutions.',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <span className="text-lg text-gray-700">Loading...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <span className="text-lg text-red-600">{error}</span>
      </div>
    );
  }

  const visiblePaginatedAgencies = paginatedAgencies
    .filter((agency) => agency.isApproved)
    .filter((agency) => {
      return selectedIndustries.length === 0 || selectedIndustries.includes(agency.industry);
    });

  return (
    <>
      <Helmet>
        <title>AI Agency Archive | Discover Top Rated AI Agencies</title>
        <meta
          name="description"
          content="Explore the AI Agency Directory archive to find top AI agencies, consultants, and experts. Discover AI services, tools, and solutions. Start your search today!"
        />
      </Helmet>

      <PageHead
        pagename="Discover Leading AI Agencies"
        subheading="Start by telling us what you need or browse the full directory of AI agencies."
      />

      <div className="container mx-auto my-6 px-4 max-w-6xl">
        {mode === 'choice' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-8 rounded-2xl bg-primary text-white flex flex-col justify-between shadow-lg">
              <div>
                <h2 className="text-3xl font-bold mb-3">Tell us what you're looking for</h2>
                <p className="text-lg mb-6">
                  Answer a few quick questions and we will highlight agencies that fit your project.
                </p>
                <button
                  onClick={() => {
                    setMode('guided');
                    setGuidedStep(0);
                    setRecommendedAgencies([]);
                  }}
                  className="inline-flex items-center px-6 py-3 bg-white text-primary font-semibold rounded-full shadow-md hover:bg-gray-100 transition"
                >
                  Get matched to agencies
                  <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                </button>
              </div>
              <div className="mt-6 flex items-center space-x-2 text-sm opacity-90">
                <FontAwesomeIcon icon={faStar} className="text-yellow-300" />
                <span>Smart matching based on your goals</span>
              </div>
            </div>

            <div className="p-8 rounded-2xl bg-gray-100 flex flex-col justify-between shadow-md">
              <div>
                <h2 className="text-3xl font-bold mb-3 text-secondary">Browse the full directory</h2>
                <p className="text-lg mb-6 text-gray-700">
                  Prefer to explore on your own? View all agencies, filter by services, industry, and more.
                </p>
                <button
                  onClick={() => setMode('browse')}
                  className="inline-flex items-center px-6 py-3 bg-primary text-white font-semibold rounded-full shadow-md hover:bg-primary/90 transition"
                >
                  Browse freely
                  <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                </button>
              </div>
              <div className="mt-6 text-sm text-gray-600">
                You can always come back and use guided matching later.
              </div>
            </div>
          </div>
        )}

        {mode === 'guided' && (
          <div className="mt-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-secondary mb-2">
                  Tell us about your AI project
                </h2>
                <p className="text-gray-600">
                  Answer a few questions so we can highlight agencies that match your needs.
                </p>
              </div>
              <button
                onClick={() => {
                  setMode('browse');
                  setGuidedStep(0);
                }}
                className="mt-4 md:mt-0 px-4 py-2 border border-primary text-primary rounded-full text-sm font-medium hover:bg-primary hover:text-white transition"
              >
                Skip to browse
              </button>
            </div>

            <div className="flex items-center mb-6 space-x-2 text-sm text-gray-600">
              {guidedStepsConfig.map((step, index) => (
                <React.Fragment key={step.key}>
                  <button
                    type="button"
                    onClick={() => setGuidedStep(index)}
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${
                      guidedStep === index
                        ? 'bg-primary text-white'
                        : index < guidedStep
                        ? 'bg-primary/10 text-primary'
                        : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    {index + 1}
                  </button>
                  {index < guidedStepsConfig.length - 1 && (
                    <div className="flex-1 h-px bg-gray-300 hidden md:block" />
                  )}
                </React.Fragment>
              ))}
            </div>

            <div className="bg-white rounded-2xl shadow-md p-6 md:p-8">
              {(() => {
                const step = guidedStepsConfig[guidedStep];
                if (!step) return null;
                return (
                  <>
                    <h3 className="text-xl font-bold text-secondary mb-2">{step.title}</h3>
                    <p className="text-gray-600 mb-4">{step.description}</p>

                    {step.type === 'textarea' && (
                      <textarea
                        value={guidedAnswers[step.key]}
                        onChange={(e) => handleGuidedChange(step.key, e.target.value)}
                        rows={4}
                        className="w-full border border-primary/40 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="Describe in your own words..."
                      />
                    )}

                    {step.type === 'selectIndustry' && (
                      <div className="space-y-3">
                        <select
                          value={guidedAnswers.industryPreference}
                          onChange={(e) =>
                            handleGuidedChange('industryPreference', e.target.value)
                          }
                          className="w-full border border-primary/40 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                          <option value="">I am not sure / Any industry</option>
                          {industries.map((industry) => (
                            <option key={industry.id} value={industry.name}>
                              {industry.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {step.type === 'multiSelectServices' && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {services.map((service) => {
                          const selected = guidedAnswers.servicePreferences.includes(
                            service.name
                          );
                          return (
                            <button
                              type="button"
                              key={service.id}
                              onClick={() => handleGuidedServiceToggle(service.name)}
                              className={`flex items-center justify-between px-4 py-3 rounded-lg border text-left text-sm font-medium transition ${
                                selected
                                  ? 'bg-primary text-white border-primary'
                                  : 'bg-white text-gray-800 border-gray-300 hover:border-primary'
                              }`}
                            >
                              <span>{service.name}</span>
                              {selected && (
                                <span className="ml-2 text-xs bg-white/20 rounded-full px-2 py-1">
                                  Selected
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {step.type === 'selectBudget' && (
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {[
                          { value: 'starter', label: 'Starter', desc: 'Exploring AI options' },
                          {
                            value: 'growing',
                            label: 'Growing',
                            desc: 'Serious pilot or rollout',
                          },
                          { value: 'scale', label: 'Scale', desc: 'Enterprise scale project' },
                        ].map((opt) => {
                          const selected = guidedAnswers.budgetRange === opt.value;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => handleGuidedChange('budgetRange', opt.value)}
                              className={`p-4 rounded-xl border text-left transition flex flex-col space-y-1 ${
                                selected
                                  ? 'bg-primary text-white border-primary'
                                  : 'bg-white text-gray-800 border-gray-300 hover:border-primary'
                              }`}
                            >
                              <span className="font-semibold">{opt.label}</span>
                              <span className={`text-xs ${selected ? 'text-white/80' : 'text-gray-600'}`}>
                                {opt.desc}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    <div className="flex justify-between items-center mt-8">
                      <button
                        type="button"
                        onClick={handleGuidedBack}
                        className="inline-flex items-center px-4 py-2 rounded-full border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-100"
                      >
                        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                        {guidedStep === 0 ? 'Back to choices' : 'Back'}
                      </button>
                      <button
                        type="button"
                        onClick={handleGuidedNext}
                        className="inline-flex items-center px-5 py-2.5 rounded-full bg-primary text-white text-sm font-semibold hover:bg-primary/90"
                      >
                        {guidedStep === guidedStepsConfig.length - 1
                          ? 'See matched agencies'
                          : 'Next'}
                        <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>

            <div className="mt-10">
              <h3 className="text-2xl font-bold text-secondary mb-4">
                Recommended agencies based on your answers
              </h3>
              {recommendedAgencies.length === 0 ? (
                <p className="text-gray-600">
                  Once you complete all steps, we will highlight agencies that match your criteria.
                </p>
              ) : (
                <ul className="space-y-6">
                  {recommendedAgencies.map((agency) => (
                    <li
                      key={agency.id}
                      className="p-4 bg-gray-100 border border-gray-200 rounded-xl shadow-sm"
                    >
                      <div className="grid w-full grid-cols-1 md:grid-cols-4 md:items-center md:space-x-6">
                        <div className="col-span-1 bg-gray-200 p-4 flex items-center justify-center rounded-lg">
                          <Link to={`/agency/${agency.id}`}>
                            <img
                              loading="lazy"
                              src={agency.logo || '/placeholder.png'}
                              alt={agency.name}
                              className="w-32 h-32 md:w-40 md:h-40 object-contain rounded-full bg-white"
                            />
                          </Link>
                        </div>
                        <div className="col-span-3">
                          <div className="flex flex-col justify-between space-y-4">
                            <Link to={`/agency/${agency.id}`}>
                              <h2 className="text-xl md:text-2xl font-bold text-gray-900">
                                {agency.name}
                              </h2>
                            </Link>
                            <div>
                              <p className="text-gray-600">
                                <span className="font-bold">Industry:</span> {agency.industry}
                              </p>
                              <p className="text-gray-600">
                                <span className="font-bold">Created on:</span>{' '}
                                {agency.dateCreated?.seconds
                                  ? new Date(
                                      agency.dateCreated.seconds * 1000
                                    ).toLocaleDateString()
                                  : 'N/A'}
                              </p>
                            </div>
                            <p className="text-gray-700">
                              {agency.description && agency.description.length > 300
                                ? agency.description.slice(0, 300) + '...'
                                : agency.description}
                            </p>
                            <span className="text-primary m-0 font-bold">
                              <Link to={`/agency/${agency.id}`}>Read More</Link>
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-4">
                        {agency.services?.map((service, index) => (
                          <span
                            key={index}
                            className="bg-blue-200 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded"
                          >
                            {service}
                          </span>
                        ))}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

{mode === 'results' && (
  <div className="mt-8">
    <h2 className="text-3xl font-bold text-secondary mb-6">
      Your Matched Agencies
    </h2>

    {recommendedAgencies.length === 0 ? (
      <p className="text-gray-600">
        No agencies match your criteria. Try adjusting your answers.
      </p>
    ) : (
      <ul className="space-y-6">
        {recommendedAgencies.map((agency) => (
          <li
            key={agency.id}
            className="p-4 bg-gray-100 border border-gray-200 rounded-xl shadow-sm"
          >
            <div className="grid w-full grid-cols-1 md:grid-cols-4 md:items-center md:space-x-6">
              <div className="col-span-1 bg-gray-200 p-4 flex items-center justify-center rounded-lg">
                <Link to={`/agency/${agency.id}`}>
                  <img
                    loading="lazy"
                    src={agency.logo || '/placeholder.png'}
                    alt={agency.name}
                    className="w-32 h-32 md:w-40 md:h-40 object-contain rounded-full bg-white"
                  />
                </Link>
              </div>
              <div className="col-span-3">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                  {agency.name}
                </h2>
                <p className="text-gray-600">
                  <span className="font-bold">Industry:</span> {agency.industry}
                </p>
                <p className="text-gray-600 mb-3">
                  <span className="font-bold">Created on:</span>{' '}
                  {agency.dateCreated?.seconds
                    ? new Date(agency.dateCreated.seconds * 1000).toLocaleDateString()
                    : 'N/A'}
                </p>
                <p className="text-gray-700 mb-2">
                  {agency.description?.slice(0, 200)}...
                </p>
                <Link
                  to={`/agency/${agency.id}`}
                  className="text-primary font-bold"
                >
                  View Profile
                </Link>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mt-4">
              {agency.services?.map((service, index) => (
                <span
                  key={index}
                  className="bg-blue-200 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded"
                >
                  {service}
                </span>
              ))}
            </div>
          </li>
        ))}
      </ul>
    )}

    <button
      onClick={() => setMode('guided')}
      className="mt-6 px-6 py-2 bg-primary text-white rounded-full"
    >
      Back to questionnaire
    </button>
  </div>
)}


        {mode === 'browse' && (
          <div className="mt-8 bg-white rounded-2xl shadow-md p-6 md:p-8">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-4">
                <h1 className="text-3xl font-bold text-secondary flex items-center gap-2">
                  Featured Agencies
                  <FontAwesomeIcon icon={faStar} className="text-yellow-400" />
                </h1>
                <button
                  onClick={() => {
                    setMode('guided');
                    setGuidedStep(0);
                    setRecommendedAgencies([]);
                  }}
                  className="hidden md:inline-flex items-center px-4 py-2 rounded-full border border-primary text-primary text-sm font-semibold hover:bg-primary hover:text-white"
                >
                  Need help choosing?
                </button>
              </div>
              <Top label="featured" />
            </div>

            <div className="max-w-7xl mx-auto mt-10">
              <label
                id="nextScroll"
                className="font-bold text-2xl text-primary block mb-4"
              >
                Find what you're looking for:
              </label>

              <div className="flex flex-col md:flex-row justify-between my-2 items-start md:items-center space-x-0 md:space-x-3 space-y-1">
                <select
                  value={searchOption}
                  onChange={(e) => setSearchOption(e.target.value)}
                  className="px-4 py-2 border border-primary rounded-md shadow-sm focus:outline-none"
                >
                  <option value="name">Search by Name</option>
                  <option value="service">Search by Service</option>
                  <option value="industry">Search by Industry</option>
                </select>
                <input
                  type="text"
                  placeholder={`Search by ${searchOption}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-primary rounded-md shadow-lg focus:outline-none"
                />
                <FontAwesomeIcon
                  icon={faSearch}
                  className="text-xl text-primary hidden sm:block"
                />
              </div>

              <div className="my-4 flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4 items-start md:items-center">
                <label className="font-bold text-lg text-primary">Sort by:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-4 py-2 border border-primary rounded-md shadow-sm focus:outline-none"
                >
                  <option value="latest">Latest</option>
                  <option value="oldest">Oldest</option>
                </select>

                <div className="relative inline-block">
                  <button
                    onClick={() => {
                      setShowIndustriesDropdown(!showIndustriesDropdown);
                      setShowServicesDropdown(false);
                    }}
                    className="px-4 py-2 border border-primary rounded-md shadow-sm focus:outline-none flex items-center"
                  >
                    {selectedIndustries.length > 0
                      ? `${selectedIndustries.length} Selected`
                      : 'Select Industries'}{' '}
                    <FontAwesomeIcon icon={faChevronDown} className="ml-2" />
                  </button>
                  {showIndustriesDropdown && (
                    <div className="absolute z-10 mt-1 w-56 bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-auto">
                      {industries.map((industry) => (
                        <label
                          key={industry.id}
                          className="flex items-center px-4 py-2 text-sm cursor-pointer hover:bg-gray-50"
                        >
                          <input
                            type="checkbox"
                            checked={selectedIndustries.includes(industry.name)}
                            onChange={() => toggleIndustry(industry.name)}
                            className="mr-2"
                          />
                          {industry.name}
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                <div className="relative inline-block">
                  <button
                    onClick={() => {
                      setShowServicesDropdown(!showServicesDropdown);
                      setShowIndustriesDropdown(false);
                    }}
                    className="px-4 py-2 border border-primary rounded-md shadow-sm focus:outline-none flex items-center"
                  >
                    {selectedServices.length > 0
                      ? `${selectedServices.length} Selected`
                      : 'Select Services'}{' '}
                    <FontAwesomeIcon icon={faChevronDown} className="ml-2" />
                  </button>
                  {showServicesDropdown && (
                    <div className="absolute z-10 mt-1 w-56 bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-auto">
                      {services.map((service) => (
                        <label
                          key={service.id}
                          className="flex items-center px-4 py-2 text-sm cursor-pointer hover:bg-gray-50"
                        >
                          <input
                            type="checkbox"
                            checked={selectedServices.includes(service.name)}
                            onChange={() => toggleService(service.name)}
                            className="mr-2"
                          />
                          {service.name}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <ul className="space-y-6 md:space-y-8 min-h-[400px] mt-4">
                {visiblePaginatedAgencies.length === 0 ? (
                  <p className="text-gray-500 text-center">No agencies match your filters.</p>
                ) : (
                  visiblePaginatedAgencies.map((agency) => (
                    <li
                      key={agency.id}
                      className="p-4 bg-gray-200 border border-gray-200 rounded-xl shadow-sm"
                    >
                      <div className="grid w-full grid-cols-1 md:grid-cols-4 md:items-center md:space-x-6">
                        <div className="col-span-1 bg-gray-300 p-4 flex items-center justify-center rounded-lg">
                          <Link to={`/agency/${agency.id}`}>
                            <img
                              loading="lazy"
                              src={agency.logo || '/placeholder.png'}
                              alt={agency.name}
                              className="w-48 h-48 object-contain rounded-full bg-white"
                            />
                          </Link>
                        </div>
                        <div className="col-span-3">
                          <div className="flex flex-col justify-between space-y-4">
                            <Link to={`/agency/${agency.id}`}>
                              <h2 className="text-xl md:text-2xl font-bold text-gray-900">
                                {agency.name}
                              </h2>
                            </Link>
                            <div>
                              <p className="text-gray-600">
                                <span className="font-bold">Industry:</span> {agency.industry}
                              </p>
                              <p className="text-gray-600">
                                <span className="font-bold">Created on:</span>{' '}
                                {agency.dateCreated?.seconds
                                  ? new Date(
                                      agency.dateCreated.seconds * 1000
                                    ).toLocaleDateString()
                                  : 'N/A'}
                              </p>
                            </div>
                            <p className="text-gray-700">
                              {agency.description && agency.description.length > 300
                                ? agency.description.slice(0, 300) + '...'
                                : agency.description}
                            </p>
                            <span className="text-primary m-0 font-bold">
                              <Link to={`/agency/${agency.id}`}>Read More</Link>
                            </span>
                          </div>
                        </div>
                        <div className="col-span-4">
                          <div className="flex flex-wrap gap-2 mt-4">
                            {agency.services?.map((service, index) => (
                              <span
                                key={index}
                                className="bg-blue-200 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded"
                              >
                                {service}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))
                )}
              </ul>

              <div className="flex justify-center items-center mt-6 space-x-9">
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-primary text-white rounded-md disabled:opacity-50"
                >
                  Previous
                </button>
                <span>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 bg-primary text-white rounded-md disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <NewsletterSignup />

      <div className="bg-white py-16 px-6">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-bold text-primary mb-12">
            Why Choose Our AI Agency Directory?
          </h2>
          <p className="text-xl text-gray-700 mb-16">
            We provide a curated list of top AI agencies, verified reviews, and detailed profiles
            to help you find the perfect AI partner.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 font-semibold">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="p-8 rounded-lg shadow-lg shadow-secondary flex flex-col items-center space-y-2"
              >
                <div className="bg-primary p-5 rounded-full flex items-center justify-center w-16 h-16">
                  <FontAwesomeIcon icon={benefit.icon} className="text-3xl text-white" />
                </div>
                <h3 className="text-2xl text-secondary font-bold mb-4">{benefit.title}</h3>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-gray-100 py-16">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-10 text-primary">
            Frequently Asked Questions (FAQs)
          </h2>

          <div className="space-y-6">
            <div className="bg-white shadow-md p-6 rounded-lg">
              <div
                className="flex justify-between items-center cursor-pointer"
                onClick={() => toggleAnswer(0)}
              >
                <h3 className="text-lg font-semibold text-secondary">
                  How do I find the right AI agency for my business?
                </h3>
                <FontAwesomeIcon
                  icon={activeIndex === 0 ? faChevronUp : faChevronDown}
                  className="text-primary"
                />
              </div>
              <div
                className={`overflow-hidden transition-all duration-500 ease-in-out ${
                  activeIndex === 0 ? 'max-h-screen' : 'max-h-0'
                }`}
              >
                <p className="mt-4 text-gray-600">
                  Use our guided questions to get matched automatically, or use search and filters
                  to narrow down agencies by services and industry.
                </p>
              </div>
            </div>

            <div className="bg-white shadow-md p-6 rounded-lg">
              <div
                className="flex justify-between items-center cursor-pointer"
                onClick={() => toggleAnswer(1)}
              >
                <h3 className="text-lg font-semibold text-secondary">
                  How are the agencies verified?
                </h3>
                <FontAwesomeIcon
                  icon={activeIndex === 1 ? faChevronUp : faChevronDown}
                  className="text-primary"
                />
              </div>
              <div
                className={`overflow-hidden transition-all duration-500 ease-in-out ${
                  activeIndex === 1 ? 'max-h-screen' : 'max-h-0'
                }`}
              >
                <p className="mt-4 text-gray-600">
                  All agencies undergo a thorough vetting process to ensure they meet our quality
                  standards.
                </p>
              </div>
            </div>

            <div className="bg-white shadow-md p-6 rounded-lg">
              <div
                className="flex justify-between items-center cursor-pointer"
                onClick={() => toggleAnswer(2)}
              >
                <h3 className="text-lg font-semibold text-secondary">
                  What services do AI agencies typically offer?
                </h3>
                <FontAwesomeIcon
                  icon={activeIndex === 2 ? faChevronUp : faChevronDown}
                  className="text-primary"
                />
              </div>
              <div
                className={`overflow-hidden transition-all duration-500 ease-in-out ${
                  activeIndex === 2 ? 'max-h-screen' : 'max-h-0'
                }`}
              >
                <p className="mt-4 text-gray-600">
                  AI agencies often provide services such as workflow automation, data labeling,
                  chatbots, AI strategy consulting, and custom model development.
                </p>
              </div>
            </div>

            <div className="bg-white shadow-md p-6 rounded-lg">
              <div
                className="flex justify-between items-center cursor-pointer"
                onClick={() => toggleAnswer(3)}
              >
                <h3 className="text-lg font-semibold text-secondary">What is an AI agency?</h3>
                <FontAwesomeIcon
                  icon={activeIndex === 3 ? faChevronUp : faChevronDown}
                  className="text-primary"
                />
              </div>
              <div
                className={`overflow-hidden transition-all duration-500 ease-in-out ${
                  activeIndex === 3 ? 'max-h-screen' : 'max-h-0'
                }`}
              >
                <p className="mt-4 text-gray-600">
                  An AI agency is a company that provides artificial intelligence solutions,
                  services, or consultancy to help businesses automate processes, analyze data, or
                  implement AI-driven technologies. They often specialize in machine learning,
                  natural language processing, and AI-powered automation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AgencyArchive;
